import { api } from './client'

export interface MyReport {
  id: string
  reporter_id: string
  target_type: string
  target_id: string
  reason: string
  description?: string
  status: 'pending' | 'resolved' | 'dismissed'
  action_taken?: string
  created_at: string
  resolved_at?: string
}

export interface QueueReport {
  id: string
  reporter_id: string
  target_type: string
  target_id: string
  reason: string
  description?: string
  status: 'pending' | 'resolved' | 'dismissed'
  created_at: string
}

export interface ModerationDashboard {
  pending_reports: number
  resolved_today: number
  active_bans: number
  total_reports: number
}

export const moderationApi = {
  listMyReports: (): Promise<{ reports: MyReport[] }> =>
    api.get('/v1/moderation/reports/mine'),

  getQueue: (): Promise<{ reports: QueueReport[] }> =>
    api.get('/v1/moderation/queue'),

  resolveReport: (reportId: string, actionTaken?: string): Promise<void> =>
    api.post(`/v1/moderation/queue/${reportId}/resolve`, { action_taken: actionTaken }),

  dismissReport: (reportId: string): Promise<void> =>
    api.post(`/v1/moderation/queue/${reportId}/dismiss`, {}),

  getDashboard: (): Promise<ModerationDashboard> =>
    api.get('/v1/moderation/dashboard'),

  globalBanUser: (userId: string, reason?: string): Promise<void> =>
    api.post(`/v1/moderation/users/${userId}/ban`, { reason }),

  globalUnbanUser: (userId: string): Promise<void> =>
    api.delete(`/v1/moderation/users/${userId}/ban`),

  getUserModerationHistory: (userId: string): Promise<{ actions: Array<{ type: string; reason?: string; created_at: string }> }> =>
    api.get(`/v1/moderation/users/${userId}/history`),

  banUser: (groupId: string, userId: string, reason?: string): Promise<void> =>
    api.post(`/v1/moderation/groups/${groupId}/ban`, { user_id: userId, reason }),

  unbanUser: (groupId: string, userId: string): Promise<void> =>
    api.delete(`/v1/moderation/groups/${groupId}/ban/${userId}`),

  muteUser: (groupId: string, userId: string, durationSeconds?: number): Promise<void> =>
    api.post(`/v1/moderation/groups/${groupId}/mute`, { user_id: userId, duration_seconds: durationSeconds }),

  unmuteUser: (groupId: string, userId: string): Promise<void> =>
    api.delete(`/v1/moderation/groups/${groupId}/mute/${userId}`),

  fileReport: (targetId: string, reason: string, type: 'spam' | 'abuse' | 'other' = 'other'): Promise<void> =>
    api.post('/v1/moderation/reports', { target_id: targetId, reason, type }),

  listWordFilters: (groupId: string): Promise<{ filters: string[] }> =>
    api.get(`/v1/moderation/groups/${groupId}/filters`),

  addWordFilter: (groupId: string, word: string): Promise<void> =>
    api.post(`/v1/moderation/groups/${groupId}/filters`, { word }),

  removeWordFilter: (groupId: string, word: string): Promise<void> =>
    api.delete(`/v1/moderation/groups/${groupId}/filters/${encodeURIComponent(word)}`),

  getGroupConfig: (groupId: string): Promise<{ anti_spam: boolean; max_messages_per_minute: number }> =>
    api.get(`/v1/moderation/groups/${groupId}/config`),

  updateGroupConfig: (groupId: string, config: { anti_spam?: boolean; max_messages_per_minute?: number }): Promise<void> =>
    api.put(`/v1/moderation/groups/${groupId}/config`, config),

  shadowBanUser: (userId: string, reason?: string): Promise<void> =>
    api.post(`/v1/moderation/users/${userId}/shadow-ban`, { reason }),

  unshadowBanUser: (userId: string): Promise<void> =>
    api.delete(`/v1/moderation/users/${userId}/shadow-ban`),

  warnUser: (userId: string, reason?: string): Promise<void> =>
    api.post(`/v1/moderation/users/${userId}/warn`, { reason }),

  verifyUser: (userId: string): Promise<void> =>
    api.post(`/v1/moderation/users/${userId}/verify`, {}),

  unverifyUser: (userId: string): Promise<void> =>
    api.delete(`/v1/moderation/users/${userId}/verify`),

  getUserStatus: (userId: string): Promise<{ is_banned: boolean; is_shadow_banned: boolean; is_verified: boolean; ban_reason?: string }> =>
    api.get(`/v1/moderation/users/${userId}/status`),
}
